import { makeAutoObservable, runInAction } from 'mobx'
import { FeaturePageService } from '~/shared/api/services/feature-page'
import { handleAxiosError } from '~/shared/lib'
import { FeatureLoadingType, FeaturePageParams } from './types'
import { FeatureType } from '~/shared/api/services/feature-page/types'
import { INITIAL_FORM_FEATURE } from '../constants/initial-form-feature'

export class FeaturePageStore {
  features: FeatureType[] = []

  formDataFeature: FeatureType = INITIAL_FORM_FEATURE

  changedLocalizationCodes: string[] = []

  initialLocalizationTitles: Record<string, string> = {}

  loaders: FeatureLoadingType = {
    feature: false,
    loadMoreFeature: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  searchParams = {
    q: '',
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof FeatureLoadingType) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof FeatureLoadingType) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  updateSearchParam = <K extends keyof typeof this.searchParams>(
    key: K,
    value: (typeof this.searchParams)[K],
  ) => {
    this.searchParams[key] = value
  }

  getFeaturePage = async (options?: { action?: 'loadMoreFeature' }) => {
    const { action } = options || {}
    this.activeLoader(action || 'feature')

    try {
      const params: FeaturePageParams = {
        limit: this.requestSettings.limit,
        offset: action === 'loadMoreFeature' ? this.requestSettings.offset : 0,
      }

      if (this.searchParams.q?.trim()) {
        params.q = this.searchParams.q.trim()
      }

      const { data } = await FeaturePageService.getFeatures({
        params,
      })

      if (action === 'loadMoreFeature') {
        runInAction(() => {
          this.features = [...this.features, ...data]
        })
      } else {
        runInAction(() => {
          this.features = data
        })
      }
      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreFeature'
              ? this.requestSettings.offset + this.requestSettings.limit
              : 20,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'feature')
    }
  }

  getFeatureById = (feature: FeatureType) => {
    this.formDataFeature = feature
    this.initialLocalizationTitles = Object.fromEntries(
      feature.localizations.map((local) => [
        local.languageCode.toUpperCase(),
        local.title || '',
      ]),
    )
    this.changedLocalizationCodes = []
  }

  clearFormData = () => {
    this.formDataFeature = INITIAL_FORM_FEATURE
    this.initialLocalizationTitles = {}
    this.changedLocalizationCodes = []
  }

  updateFormField = (languageCode: string, value: string) => {
    const normalizedLanguageCode = languageCode.toUpperCase()
    const originalTitle = this.initialLocalizationTitles[normalizedLanguageCode]
    this.changedLocalizationCodes =
      value === originalTitle
        ? this.changedLocalizationCodes.filter(
            (code) => code !== normalizedLanguageCode,
          )
        : [
            ...new Set([
              ...this.changedLocalizationCodes,
              normalizedLanguageCode,
            ]),
          ]

    this.formDataFeature = {
      ...this.formDataFeature,

      localizations: this.formDataFeature.localizations.map((local) =>
        local.languageCode.toUpperCase() === normalizedLanguageCode
          ? { ...local, title: value }
          : local,
      ),
    }
  }
}
